/**
 * The addresses of the application. Every management view lives under its organization, so a link always
 * states the scope it belongs to; `patterns` is the same table for the router and for the return-destination
 * allowlist of the identity module.
 */
export class Paths {
	static readonly patterns = {
		home: '/',
		signin: '/signin',
		returning: '/signin/return',
		waitlist: '/waitlist',
		access: '/access',
		notifications: '/notifications',
		organization: '/o/:organization',
		applications: '/o/:organization/applications',
		projects: '/o/:organization/projects',
		application: '/o/:organization/applications/:application',
		consumption: '/o/:organization/consumption',
		plan: '/o/:organization/plan',
		providers: '/o/:organization/providers'
	} as const;

	static readonly sections = ['registrations', 'jobs', 'releases', 'domains', 'access'] as const;

	#organization: string;

	constructor(organization: string) {
		this.#organization = encodeURIComponent(organization);
	}

	/** The organization's home in the administration, which opens its applications */
	get home() {
		return `/o/${this.#organization}`;
	}

	get applications() {
		return `/o/${this.#organization}/applications`;
	}

	/** The organization's projects in Beyond Projects, with what each has in CDN */
	get projects() {
		return `/o/${this.#organization}/projects`;
	}

	/** The task page that registers an application */
	get create() {
		return `/o/${this.#organization}/applications/new`;
	}

	/**
	 * The publication task of a capture (`publish?capture=&project=`), which another product opens; `from` keeps the
	 * product it was opened from, whose arrival line stays the way back, and `application` the application a task of
	 * this administration returns with (one it defined a target for, or registered), which the task chooses again
	 */
	publish(capture: string, project?: string, from?: string, application?: string) {
		const query = new URLSearchParams({ capture, ...(project ? { project } : {}), ...(from ? { from } : {}), ...(application ? { application } : {}) });
		return `/o/${this.#organization}/publish?${query}`;
	}

	/** The task page that adds an application of no project to the project `project` */
	adopt(project: string) {
		return `/o/${this.#organization}/applications/adopt?project=${encodeURIComponent(project)}`;
	}

	/** The task page that adds a package registry, or sets the one named `name` */
	provider(name?: string) {
		return `${this.providers}/${name ? encodeURIComponent(name) : 'new'}`;
	}

	get consumption() {
		return `/o/${this.#organization}/consumption`;
	}

	get plan() {
		return `/o/${this.#organization}/plan`;
	}

	get providers() {
		return `/o/${this.#organization}/providers`;
	}

	application(id: string, section?: (typeof Paths.sections)[number]) {
		return `${this.applications}/${encodeURIComponent(id)}${section ? `/${section}` : ''}`;
	}

	job(application: string, job: string) {
		return `${this.application(application, 'jobs')}/${encodeURIComponent(job)}`;
	}

	release(application: string, release: string) {
		return `${this.application(application, 'releases')}/${encodeURIComponent(release)}`;
	}

	/** The task page that registers dependency selections for an application */
	register(application: string) {
		return `${this.application(application, 'registrations')}/new`;
	}

	/** The task page that changes an application's settings */
	settings(application: string) {
		return `${this.application(application)}/settings`;
	}

	/** The task page that defines a target of an application */
	target(application: string) {
		return `${this.application(application)}/targets/new`;
	}

	/** The task page that invites a guest to a private application */
	invite(application: string) {
		return `${this.application(application, 'access')}/invite`;
	}

	/** The task page that adds a custom domain to an application */
	domain(application: string) {
		return `${this.application(application, 'domains')}/new`;
	}

	registration(application: string, registration: string) {
		return `${this.application(application, 'registrations')}/${encodeURIComponent(registration)}`;
	}
}
